// src/components/EntityDrawer.js
// Drawer laterale per la Navigazione Contestuale Intelligente
// Mostra schede di Cliente, Commessa, Dipendente, Task senza cambiare pagina
// Supporta navigazione breadcrumb (stack) per spostarsi tra entità collegate

import { TestoFormattato } from "../utils/testoFormattato";
import React, { useEffect, useState } from "react";
import { useValoriCommesse, conValore } from "../utils/valoriCommesse";
import { useNav } from "../contexts/NavContext";
import { useOrg } from "../contexts/OrgContext";
import { useAuth } from "../contexts/AuthContext";
import { collection, query, where, onSnapshot, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { X, ChevronLeft, ExternalLink, ArrowRight } from "lucide-react";
import { calcClienteHealthScore, calcIndiceFedelta } from "../utils/clienteHelpers";
import { useFeatures } from "../contexts/FeaturesContext";
import EntityLink, { CommessaLink, DipendenteLink, TaskLink } from "./EntityLink";
import { toLocalDateStr } from "../utils/dateUtils";

// ─── STILI BASE ───────────────────────────────────────────────────────────────

const DRAWER_WIDTH = 420;

function DrawerShell({ children }) {
  const { drawer, closeDrawer, goBack } = useNav();
  const hasBack = drawer?.stack?.length > 0;

  return (
    <>
      {/* Overlay semitrasparente */}
      <div
        onClick={closeDrawer}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.18)",
          zIndex: 1000, backdropFilter: "blur(1px)",
        }}
      />
      {/* Pannello laterale */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: DRAWER_WIDTH, background: "#fff",
        boxShadow: "-4px 0 32px rgba(0,0,0,0.15)",
        zIndex: 1001, display: "flex", flexDirection: "column",
        animation: "slideInRight 0.22s ease",
      }}>
        {/* Topbar drawer */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "12px 16px", borderBottom: "1px solid #e5e7eb",
          background: "#faf5ff", flexShrink: 0,
        }}>
          {hasBack && (
            <button onClick={goBack} style={btnIcon} title="Indietro">
              <ChevronLeft size={18} color="#7c3aed" />
            </button>
          )}
          {/* Breadcrumb stack */}
          {drawer?.stack?.map((item, i) => (
            <React.Fragment key={i}>
              <span style={{ fontSize: 11, color: "#9ca3af", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {entityLabel(item.type, item.data)}
              </span>
              <ArrowRight size={10} color="#d1d5db" />
            </React.Fragment>
          ))}
          <span style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {drawer ? entityLabel(drawer.type, drawer.data) : ""}
          </span>
          <button onClick={closeDrawer} style={btnIcon} title="Chiudi">
            <X size={18} color="#6b7280" />
          </button>
        </div>
        {/* Contenuto */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {children}
        </div>
      </div>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(60px); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
      `}</style>
    </>
  );
}

function entityLabel(type, data) {
  if (!data) return "";
  switch (type) {
    case "cliente":    return data.ragioneSociale || data.nome || "Cliente";
    case "commessa":   return data.name || "Commessa";
    case "dipendente": return `${data.nome || ""} ${data.cognome || ""}`.trim() || "Dipendente";
    case "task":       return data.title || "Attività";
    default:           return "Scheda";
  }
}

const btnIcon = {
  background: "none", border: "none", cursor: "pointer",
  borderRadius: 6, padding: 4, display: "flex", alignItems: "center",
  transition: "background 0.15s",
};

// ─── SCHEDA CLIENTE ──────────────────────────────────────────────────────────

function DrawerCliente({ data }) {
  const { navigateFull, openEntity } = useNav();
  const { effectiveOrgId } = useOrg();
  const { isOrgAdmin } = useAuth();
  const { hasFeature } = useFeatures();
  const isPro = hasFeature("kpi"); // piano Pro (listino Rev1)

  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!effectiveOrgId || !data?.id) return;
    const q = query(collection(db, "projects"),
      where("organizationId", "==", effectiveOrgId),
      where("clienteId", "==", data.id));
    const unsub = onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !p.isParent);
      list.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setCommesse(list);
      setLoading(false);
    });
    return () => unsub();
  }, [effectiveOrgId, data?.id]);

  // W420: valore di ogni commessa dal budget (importo + varianti accettate)
  const valoriCommesse = useValoriCommesse(effectiveOrgId, isOrgAdmin || isPro);
  const commesseV = conValore(commesse, valoriCommesse);
  const valTot = isOrgAdmin ? commesseV.reduce((s, c) => s + (parseFloat(c.valoreEconomico) || 0), 0) : 0;
  const health = isPro ? calcClienteHealthScore(commesseV) : null;
  const fedelta = isPro ? calcIndiceFedelta(commesseV) : null;
  const statoColor = { Attivo: "#059669", Potenziale: "#2563eb", Inattivo: "#9ca3af", Sospeso: "#d97706" };

  return (
    <div style={{ padding: 20 }}>
      {/* Header cliente */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 12, flexShrink: 0,
          background: "linear-gradient(135deg,#7c3aed,#4f46e5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, color: "#fff", fontWeight: 800,
        }}>
          {(data.ragioneSociale || data.nome || "?")[0].toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#111827" }}>
            {data.ragioneSociale || data.nome}
          </div>
          {data.stato && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
              background: `${statoColor[data.stato]}20`, color: statoColor[data.stato],
            }}>{data.stato}</span>
          )}
          {data.settore && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{data.settore}</div>}
        </div>
      </div>

      {/* KPI */}
      {isPro && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
          <KPICard label="Commesse" value={commesse.length} color="#7c3aed" />
          {isOrgAdmin && <KPICard label="Valore tot." value={`€ ${valTot.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} color="#059669" small />}
          {health && <KPICard label="Health" value={`${health.score}`} color={health.color} />}
        </div>
      )}

      {/* Contatti */}
      {(data.telefono || data.email || data.piva || data.codiceFiscale) && (
        <section style={sectionStyle}>
          <SectionTitle>Contatti</SectionTitle>
          {data.piva && <InfoRow label="P.IVA" value={data.piva} />}
          {data.codiceFiscale && <InfoRow label="CF" value={data.codiceFiscale} />}
          {data.telefono && <InfoRow label="Tel" value={data.telefono} />}
          {data.email && <InfoRow label="Email" value={data.email} />}
          {data.indirizzo && <InfoRow label="Indirizzo" value={data.indirizzo} />}
        </section>
      )}

      {/* Commesse collegate */}
      <section style={sectionStyle}>
        <SectionTitle>Commesse ({commesse.length})</SectionTitle>
        {loading && <div style={{ color: "#9ca3af", fontSize: 13 }}>Caricamento...</div>}
        {!loading && commesse.length === 0 && <div style={{ color: "#9ca3af", fontSize: 13 }}>Nessuna commessa</div>}
        {commesse.slice(0, 8).map(c => (
          <div key={c.id} style={linkRowStyle}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: c.color || "#7c3aed", flexShrink: 0, marginTop: 2 }} />
            <CommessaLink commessa={c} style={{ flex: 1, fontSize: 13, textAlign: "left" }}>
              {c.name}
            </CommessaLink>
            {c.archived && <span style={{ fontSize: 10, color: "#9ca3af" }}>Archiviata</span>}
          </div>
        ))}
      </section>

      {/* Referenti */}
      {data.referenti?.length > 0 && (
        <section style={sectionStyle}>
          <SectionTitle>Referenti</SectionTitle>
          {data.referenti.map((r, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{r.nome}</div>
              {r.ruolo && <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.ruolo}</div>}
              {r.telefono && <div style={{ fontSize: 12, color: "#6b7280" }}>📞 {r.telefono}</div>}
              {r.email && <div style={{ fontSize: 12, color: "#6b7280" }}>✉️ {r.email}</div>}
            </div>
          ))}
        </section>
      )}

      {/* Apri scheda completa */}
      <button
        onClick={() => navigateFull({ page: "clienti", clienteId: data.id })}
        style={openFullBtn}
      >
        <ExternalLink size={14} /> Apri scheda completa in Clienti
      </button>
    </div>
  );
}

// ─── SCHEDA COMMESSA ──────────────────────────────────────────────────────────

function DrawerCommessa({ data }) {
  const { navigateFull, openEntity } = useNav();
  const { effectiveOrgId } = useOrg();
  const { isOrgAdmin } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!effectiveOrgId || !data?.id) return;
    const q = query(collection(db, "tasks"),
      where("organizationId", "==", effectiveOrgId),
      where("projectId", "==", data.id));
    const unsub = onSnapshot(q, snap => {
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, [effectiveOrgId, data?.id]);

  useEffect(() => {
    if (!data?.members?.length) return;
    Promise.all(data.members.map(uid =>
      getDoc(doc(db, "users", uid)).then(d => d.exists() ? { uid, ...d.data() } : null)
    )).then(res => setMembers(res.filter(Boolean)));
  }, [data?.members]);

  const aperte = tasks.filter(t => t.status !== "completata" && !t.archived);
  const scadute = tasks.filter(t => {
    if (!t.dueDate || t.status === "completata") return false;
    return t.dueDate < toLocalDateStr(new Date());
  });
  const completate = tasks.filter(t => t.status === "completata");
  const progress = tasks.length > 0 ? Math.round((completate.length / tasks.length) * 100) : 0;
  // W420: il valore della commessa è il totale del budget (importo + varianti accettate)
  const valoriCommesse = useValoriCommesse(isOrgAdmin ? effectiveOrgId : null, isOrgAdmin);
  const valore = conValore([data], valoriCommesse)[0].valoreEconomico || 0;

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: data.color || "#7c3aed" }} />
          <span style={{ fontSize: 17, fontWeight: 800, color: "#111827" }}>{data.name}</span>
        </div>
        {data.clienteNome && (
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Cliente: <EntityLink type="cliente" data={{ id: data.clienteId, ragioneSociale: data.clienteNome }}>
              {data.clienteNome}
            </EntityLink>
          </div>
        )}
      </div>

      {/* KPI */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
        <KPICard label="Aperte" value={aperte.length} color="#7c3aed" />
        <KPICard label="Scadute" value={scadute.length} color={scadute.length > 0 ? "#dc2626" : "#9ca3af"} />
        <KPICard label="Completate" value={completate.length} color="#059669" />
        <KPICard label="Progresso" value={`${progress}%`} color="#2563eb" />
      </div>

      {/* Barra progresso */}
      <div style={{ background: "#f3f4f6", borderRadius: 4, height: 6, marginBottom: 20, overflow: "hidden" }}>
        <div style={{ width: `${progress}%`, height: "100%", background: "#7c3aed", borderRadius: 4, transition: "width 0.3s" }} />
      </div>

      {/* Info */}
      <section style={sectionStyle}>
        <SectionTitle>Dettagli</SectionTitle>
        {data.dataInizio && <InfoRow label="Inizio" value={data.dataInizio.split("-").reverse().join("/")} />}
        {data.dataFine && <InfoRow label="Fine" value={data.dataFine.split("-").reverse().join("/")} />}
        {isOrgAdmin && valore > 0 && (
          <InfoRow label="Valore" value={`€ ${valore.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`} />
        )}
        {data.stato && <InfoRow label="Stato" value={data.stato} />}
      </section>

      {/* Attività recenti */}
      {!loading && aperte.length > 0 && (
        <section style={sectionStyle}>
          <SectionTitle>Attività aperte ({aperte.length})</SectionTitle>
          {aperte.slice(0, 6).map(t => (
            <div key={t.id} style={linkRowStyle}>
              <div style={{
                width: 6, height: 6, borderRadius: "50%", flexShrink: 0, marginTop: 5,
                background: t.dueDate && t.dueDate < toLocalDateStr(new Date()) ? "#dc2626" : "#d1d5db",
              }} />
              <TaskLink task={t} style={{ flex: 1, fontSize: 13, textAlign: "left" }}>
                {t.title}
              </TaskLink>
              {t.assignedName && (
                <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>
                  {t.assignedName}
                </span>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Membri */}
      {members.length > 0 && (
        <section style={sectionStyle}>
          <SectionTitle>Team ({members.length})</SectionTitle>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {members.map(m => (
              <div key={m.uid} style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 20, padding: "4px 10px",
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: "#7c3aed", color: "#fff", fontSize: 10,
                  display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
                }}>
                  {(m.displayName || m.email || "?")[0].toUpperCase()}
                </div>
                <span style={{ fontSize: 12, color: "#374151" }}>{m.displayName || m.email}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <button
        onClick={() => navigateFull({ page: "project", project: data, projectId: data.id })}
        style={openFullBtn}
      >
        <ExternalLink size={14} /> Apri commessa completa
      </button>
    </div>
  );
}

// ─── SCHEDA DIPENDENTE ────────────────────────────────────────────────────────

function DrawerDipendente({ data }) {
  const { navigateFull, openEntity } = useNav();
  const { effectiveOrgId } = useOrg();
  const { isOrgAdmin } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [loading, setLoading] = useState(true);
  const uid = data?.nexoraUid || data?.uid || null;

  useEffect(() => {
    if (!effectiveOrgId || !uid) { setLoading(false); return; }
    const unsubs = [];
    unsubs.push(onSnapshot(query(collection(db, "tasks"),
      where("organizationId", "==", effectiveOrgId),
      where("assignedTo", "==", uid)),
      snap => { setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); }
    ));
    unsubs.push(onSnapshot(query(collection(db, "presenze"),
      where("orgId", "==", effectiveOrgId),
      where("uid", "==", uid)),
      snap => setPresenze(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));
    return () => unsubs.forEach(u => u());
  }, [effectiveOrgId, uid]);

  const aperte = tasks.filter(t => t.status !== "completata");
  const oreMese = presenze
    .filter(p => p.mese === new Date().getMonth() + 1 && p.anno === new Date().getFullYear())
    .reduce((s, p) => s + (p.oreTotali || 0), 0);

  const LIVELLI = { 1: "Inesperto", 2: "Poco esperto", 3: "Autonomo", 4: "Esperto", 5: "Molto esperto" };

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <div style={{
          width: 52, height: 52, borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg,#7c3aed,#4f46e5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, color: "#fff", fontWeight: 800,
        }}>
          {(data.nome || "?")[0].toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{data.nome} {data.cognome}</div>
          {data.mansione && <div style={{ fontSize: 13, color: "#6b7280" }}>{data.mansione}</div>}
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 8,
            background: data.tipo === "Dipendente" ? "#ede9fe" : data.tipo === "Subappaltatore" ? "#d1fae5" : "#dbeafe",
            color: data.tipo === "Dipendente" ? "#7c3aed" : data.tipo === "Subappaltatore" ? "#059669" : "#2563eb",
          }}>{data.tipo || "Dipendente"}</span>
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        <KPICard label="Task attivi" value={aperte.length} color="#7c3aed" />
        <KPICard label="Ore mese" value={oreMese.toFixed(0)} color="#2563eb" />
        {isOrgAdmin && data.costoOrario && (
          <KPICard label="€/ora" value={`€${data.costoOrario}`} color="#059669" />
        )}
      </div>

      {/* Info */}
      <section style={sectionStyle}>
        <SectionTitle>Informazioni</SectionTitle>
        {data.codiceFiscale && <InfoRow label="CF" value={data.codiceFiscale} />}
        {data.livello && <InfoRow label="Livello" value={LIVELLI[data.livello] || data.livello} />}
        {data.dataNascita && <InfoRow label="Data nascita" value={data.dataNascita} />}
        {uid && <InfoRow label="Account Nexiora" value="✅ Collegato" />}
      </section>

      {/* Task attivi */}
      {!loading && aperte.length > 0 && (
        <section style={sectionStyle}>
          <SectionTitle>Attività assegnate ({aperte.length})</SectionTitle>
          {aperte.slice(0, 5).map(t => (
            <div key={t.id} style={linkRowStyle}>
              <TaskLink task={t} style={{ flex: 1, fontSize: 13, textAlign: "left" }}>{t.title}</TaskLink>
              {t.projectName && <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>{t.projectName.slice(0, 15)}</span>}
            </div>
          ))}
        </section>
      )}

      {!uid ? (
        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#9a3412", marginTop: 16 }}>
          ⚠️ Questo dipendente non è ancora collegato a un account Nexiora. Collega il UID dalla scheda Dipendenti per abilitare presenze e task.
        </div>
      ) : (
        <button
          onClick={() => navigateFull({ page: "dipendenti", dipId: data.id })}
          style={openFullBtn}
        >
          <ExternalLink size={14} /> Apri scheda completa
        </button>
      )}
    </div>
  );
}

// ─── SCHEDA TASK ─────────────────────────────────────────────────────────────

function DrawerTask({ data }) {
  const { openEntity, navigateFull } = useNav();
  const { effectiveOrgId } = useOrg();
  const [subtasks, setSubtasks] = useState([]);

  useEffect(() => {
    if (!data?.id) return;
    const q = query(collection(db, "subtasks"), where("parentTaskId", "==", data.id), where("organizationId","==",effectiveOrgId));
    const unsub = onSnapshot(q, snap => setSubtasks(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [data?.id, effectiveOrgId]);

  const isScaduta = data.dueDate && data.dueDate < toLocalDateStr(new Date()) && data.status !== "completata";

  const statoColor = {
    "da fare": "#6b7280", "in corso": "#2563eb",
    "in revisione": "#d97706", "completata": "#059669", "annullata": "#dc2626",
  };

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#111827", lineHeight: 1.3, marginBottom: 8 }}>
          {data.title}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {data.status && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
              background: `${statoColor[data.status] || "#6b7280"}20`,
              color: statoColor[data.status] || "#6b7280",
            }}>{data.status}</span>
          )}
          {data.priority && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
              background: data.priority === "alta" ? "#fee2e2" : data.priority === "media" ? "#fff7ed" : "#f3f4f6",
              color: data.priority === "alta" ? "#dc2626" : data.priority === "media" ? "#d97706" : "#6b7280",
            }}>{data.priority}</span>
          )}
        </div>
      </div>

      {/* Info */}
      <section style={sectionStyle}>
        {data.dueDate && (
          <InfoRow label="Scadenza" value={data.dueDate.split("-").reverse().join("/")}
            valueStyle={{ color: isScaduta ? "#dc2626" : "#111827", fontWeight: isScaduta ? 700 : 400 }} />
        )}
        {data.assignedName && (
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Assegnato a</span>
            <EntityLink type="dipendente" data={{ id: data.assignedTo, uid: data.assignedTo, nome: data.assignedName, cognome: "" }}>
              {data.assignedName}
            </EntityLink>
          </div>
        )}
        {data.projectName && (
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Commessa</span>
            <CommessaLink commessa={{ id: data.projectId, name: data.projectName }}>
              {data.projectName}
            </CommessaLink>
          </div>
        )}
        {data.sectionName && <InfoRow label="Sezione" value={data.sectionName} />}
      </section>

      {/* Descrizione */}
      {data.description && (
        <section style={sectionStyle}>
          <SectionTitle>Descrizione</SectionTitle>
          <TestoFormattato testo={data.description} style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}/>
        </section>
      )}

      {/* Sottoattività */}
      {subtasks.length > 0 && (
        <section style={sectionStyle}>
          <SectionTitle>Sottoattività ({subtasks.length})</SectionTitle>
          {subtasks.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid #f9fafb" }}>
              <span style={{ fontSize: 14, color: s.status === "completata" ? "#059669" : "#d1d5db" }}>
                {s.status === "completata" ? "✓" : "○"}
              </span>
              <span style={{ fontSize: 13, color: s.status === "completata" ? "#9ca3af" : "#374151", textDecoration: s.status === "completata" ? "line-through" : "none" }}>
                {s.title}
              </span>
            </div>
          ))}
        </section>
      )}

      {/* Tag */}
      {data.tags?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
          {data.tags.map((tag, i) => (
            <span key={i} style={{ fontSize: 11, background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 10 }}>
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PICCOLI HELPER UI ────────────────────────────────────────────────────────

function KPICard({ label, value, color, small }) {
  return (
    <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
      <div style={{ fontSize: small ? 14 : 20, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{children}</div>;
}

function InfoRow({ label, value, valueStyle }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
      <span style={{ fontSize: 12, color: "#9ca3af" }}>{label}</span>
      <span style={{ fontSize: 13, color: "#111827", fontWeight: 500, ...valueStyle }}>{value}</span>
    </div>
  );
}

const sectionStyle = { marginBottom: 20, paddingBottom: 4 };
const linkRowStyle = { display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0", borderBottom: "1px solid #f9fafb" };
const openFullBtn = {
  display: "flex", alignItems: "center", gap: 6, width: "100%",
  justifyContent: "center", background: "#ede9fe", color: "#7c3aed",
  border: "none", borderRadius: 8, padding: "10px 16px",
  fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 8,
};

// ─── EXPORT PRINCIPALE ────────────────────────────────────────────────────────

export default function EntityDrawer() {
  const { drawer } = useNav();
  if (!drawer) return null;

  const renderContent = () => {
    switch (drawer.type) {
      case "cliente":    return <DrawerCliente data={drawer.data} />;
      case "commessa":   return <DrawerCommessa data={drawer.data} />;
      case "dipendente": return <DrawerDipendente data={drawer.data} />;
      case "task":       return <DrawerTask data={drawer.data} />;
      default:           return <div style={{ padding: 24, color: "#9ca3af" }}>Tipo non supportato</div>;
    }
  };

  return <DrawerShell>{renderContent()}</DrawerShell>;
}
